<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import countries from 'shared/constants/countries';
import {
  addDays,
  endOfMonth,
  format,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns';

const emit = defineEmits(['applyFilters', 'clearFilters']);
const { t } = useI18n();

const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');

const containerRef = ref(null);
const showAddFilterMenu = ref(false);
const showValueMenu = ref(false);
const activeFilterType = ref('');
const searchTerm = ref('');

const freshSelectedFilters = () => ({
  date: null,
  agent: null,
  group: null,
  tag: null,
  channel: null,
  assignment: null,
  availability: null,
  country: null,
});

const selectedFilters = ref(freshSelectedFilters());

const dateOptions = computed(() => [
  {
    id: 1,
    name: t('REPORT.DATE_RANGE_OPTIONS.TODAY'),
    filterValue: 'today',
  },
  {
    id: 2,
    name: t('REPORT.DATE_RANGE_OPTIONS.YESTERDAY'),
    filterValue: 'yesterday',
  },
  {
    id: 3,
    name: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS'),
    filterValue: 'last_7_days',
  },
  {
    id: 4,
    name: t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS'),
    filterValue: 'last_30_days',
  },
  {
    id: 5,
    name: t('FILTER.QA_PRESETS.LAST_MONTH'),
    filterValue: 'last_month',
  },
  {
    id: 6,
    name: t('FILTER.QA_PRESETS.CURRENT_MONTH'),
    filterValue: 'current_month',
  },
]);

const agentOptions = computed(() =>
  (agents.value || []).map(agent => ({
    id: agent.id,
    name: agent.name,
    filterValue: agent.id,
  }))
);

const groupOptions = computed(() =>
  (teams.value || []).map(team => ({
    id: team.id,
    name: team.name,
    filterValue: team.id,
  }))
);

const tagOptions = computed(() =>
  (labels.value || []).map(label => ({
    id: label.id,
    name: label.title,
    filterValue: label.title,
  }))
);

const channelOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({
    id: inbox.id,
    name: inbox.name,
    filterValue: inbox.id,
  }))
);

const assignmentOptions = computed(() => [
  {
    id: 1,
    name: t('FILTER.QA_PRESETS.ASSIGNED'),
    filterValue: 'assigned',
  },
  {
    id: 2,
    name: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.unassigned'),
    filterValue: 'unassigned',
  },
]);

const availabilityOptions = computed(() =>
  ['open', 'pending', 'resolved', 'snoozed'].map((status, index) => ({
    id: index + 1,
    name: t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status}.TEXT`),
    filterValue: status,
  }))
);

const countryOptions = computed(() =>
  (countries || []).map(country => ({
    id: country.id,
    name: country.name,
    filterValue: country.id,
  }))
);

const filterConfigs = computed(() => [
  {
    type: 'date',
    label: t('FILTER.QA_PRESETS.DATE'),
    options: dateOptions.value,
    enableSearch: false,
  },
  {
    type: 'agent',
    label: t('FILTER.QA_PRESETS.AGENT'),
    options: agentOptions.value,
    enableSearch: true,
  },
  {
    type: 'group',
    label: t('FILTER.QA_PRESETS.GROUP'),
    options: groupOptions.value,
    enableSearch: true,
  },
  {
    type: 'tag',
    label: t('FILTER.QA_PRESETS.TAG'),
    options: tagOptions.value,
    enableSearch: true,
  },
  {
    type: 'channel',
    label: t('FILTER.QA_PRESETS.CHANNEL'),
    options: channelOptions.value,
    enableSearch: true,
  },
  {
    type: 'assignment',
    label: t('FILTER.QA_PRESETS.ASSIGNMENT'),
    options: assignmentOptions.value,
    enableSearch: false,
  },
  {
    type: 'availability',
    label: t('FILTER.QA_PRESETS.AVAILABILITY'),
    options: availabilityOptions.value,
    enableSearch: false,
  },
  {
    type: 'country',
    label: t('FILTER.QA_PRESETS.COUNTRY'),
    options: countryOptions.value,
    enableSearch: true,
  },
]);

const filterConfigMap = computed(() =>
  filterConfigs.value.reduce((acc, filterConfig) => {
    acc[filterConfig.type] = filterConfig;
    return acc;
  }, {})
);

const currentConfig = computed(
  () => filterConfigMap.value[activeFilterType.value]
);

const availableFilterTypes = computed(() => {
  return filterConfigs.value.filter(
    filterConfig => !selectedFilters.value[filterConfig.type]
  );
});

const activeFilters = computed(() => {
  return filterConfigs.value
    .filter(filterConfig => selectedFilters.value[filterConfig.type])
    .map(filterConfig => ({
      type: filterConfig.type,
      label: filterConfig.label,
      option: selectedFilters.value[filterConfig.type],
    }));
});

const filteredOptions = computed(() => {
  const options = currentConfig.value?.options || [];
  if (!currentConfig.value?.enableSearch || !searchTerm.value.trim()) {
    return options;
  }

  const query = searchTerm.value.trim().toLowerCase();
  return options.filter(option =>
    String(option.name || '')
      .toLowerCase()
      .includes(query)
  );
});

const closeMenus = () => {
  showAddFilterMenu.value = false;
  showValueMenu.value = false;
  activeFilterType.value = '';
  searchTerm.value = '';
};

const emitCurrentFilters = () => {
  const payload = [];

  const addCondition = (attributeKey, filterOperator, values = []) => {
    payload.push({
      attributeKey,
      filterOperator,
      values,
      queryOperator: 'and',
    });
  };

  const toYMD = date => format(date, 'yyyy-MM-dd');

  const addDatePresetConditions = preset => {
    const today = new Date();
    let rangeStart = today;
    let rangeEnd = today;

    if (preset === 'yesterday') {
      rangeStart = subDays(today, 1);
      rangeEnd = subDays(today, 1);
    } else if (preset === 'last_7_days') {
      rangeStart = subDays(today, 6);
      rangeEnd = today;
    } else if (preset === 'last_30_days') {
      rangeStart = subDays(today, 29);
      rangeEnd = today;
    } else if (preset === 'last_month') {
      const prevMonth = subMonths(today, 1);
      rangeStart = startOfMonth(prevMonth);
      rangeEnd = endOfMonth(prevMonth);
    } else if (preset === 'current_month') {
      rangeStart = startOfMonth(today);
      rangeEnd = today;
    }

    const lowerExclusive = toYMD(subDays(rangeStart, 1));
    const upperExclusive = toYMD(addDays(rangeEnd, 1));

    addCondition('created_at', 'is_greater_than', [lowerExclusive]);
    addCondition('created_at', 'is_less_than', [upperExclusive]);
  };

  const dateFilter = selectedFilters.value.date;
  if (dateFilter?.filterValue) {
    addDatePresetConditions(dateFilter.filterValue);
  }

  const agentFilter = selectedFilters.value.agent;
  if (agentFilter?.filterValue) {
    addCondition('assignee_id', 'equal_to', [agentFilter.filterValue]);
  }

  const groupFilter = selectedFilters.value.group;
  if (groupFilter?.filterValue) {
    addCondition('team_id', 'equal_to', [groupFilter.filterValue]);
  }

  const tagFilter = selectedFilters.value.tag;
  if (tagFilter?.filterValue) {
    addCondition('labels', 'equal_to', [tagFilter.filterValue]);
  }

  const channelFilter = selectedFilters.value.channel;
  if (channelFilter?.filterValue) {
    addCondition('inbox_id', 'equal_to', [channelFilter.filterValue]);
  }

  const assignmentFilter = selectedFilters.value.assignment;
  if (assignmentFilter?.filterValue === 'assigned') {
    addCondition('assignee_id', 'is_present', []);
  } else if (assignmentFilter?.filterValue === 'unassigned') {
    addCondition('assignee_id', 'is_not_present', []);
  }

  const availabilityFilter = selectedFilters.value.availability;
  if (availabilityFilter?.filterValue) {
    addCondition('status', 'equal_to', [availabilityFilter.filterValue]);
  }

  const countryFilter = selectedFilters.value.country;
  if (countryFilter?.filterValue) {
    addCondition('country_code', 'equal_to', [countryFilter.filterValue]);
  }

  if (!payload.length) {
    emit('clearFilters');
    return;
  }

  emit('applyFilters', payload);
};

const toggleAddFilterMenu = () => {
  showValueMenu.value = false;
  activeFilterType.value = '';
  searchTerm.value = '';
  showAddFilterMenu.value = !showAddFilterMenu.value;
};

const selectFilterType = type => {
  activeFilterType.value = type;
  showAddFilterMenu.value = false;
  showValueMenu.value = true;
  searchTerm.value = '';
};

const openFilterMenu = type => {
  activeFilterType.value = type;
  showAddFilterMenu.value = false;
  showValueMenu.value = true;
  searchTerm.value = '';
};

const selectFilterOption = option => {
  if (!activeFilterType.value) return;
  selectedFilters.value[activeFilterType.value] = option;
  closeMenus();
  emitCurrentFilters();
};

const removeFilter = type => {
  selectedFilters.value[type] = null;
  if (activeFilterType.value === type) {
    closeMenus();
  }
  emitCurrentFilters();
};

const clearAllFilters = () => {
  selectedFilters.value = freshSelectedFilters();
  closeMenus();
  emit('clearFilters');
};

const onDocumentClick = event => {
  if (!containerRef.value?.contains(event.target)) {
    closeMenus();
  }
};

const onEscape = event => {
  if (event.key === 'Escape') {
    closeMenus();
  }
};

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onEscape);
});
</script>

<template>
  <div ref="containerRef" class="relative flex items-center gap-2">
    <button
      class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-xs font-semibold text-n-slate-12 hover:bg-n-alpha-1"
      type="button"
      @click.stop="toggleAddFilterMenu"
    >
      <span class="i-lucide-plus size-3.5" />
      <span>{{ $t('FILTER.ADD_NEW_FILTER') }}</span>
    </button>

    <button
      v-for="activeFilter in activeFilters"
      :key="activeFilter.type"
      class="inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-lg bg-n-alpha-2 text-xs text-n-slate-12 border border-n-weak max-w-[180px]"
      type="button"
      @click.stop="openFilterMenu(activeFilter.type)"
    >
      <span class="truncate">{{ activeFilter.label }}</span>
      <span class="text-n-slate-11 truncate"
        >is {{ activeFilter.option.name }}</span
      >
      <span
        class="i-lucide-x size-3.5 text-n-slate-10 hover:text-n-slate-12 shrink-0"
        @click.stop="removeFilter(activeFilter.type)"
      />
    </button>

    <button
      v-if="activeFilters.length"
      class="inline-flex items-center h-8 px-2 rounded-lg text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
      type="button"
      @click.stop="clearAllFilters"
    >
      {{ $t('FILTER.CLEAR_BUTTON_LABEL') }}
    </button>

    <div
      v-if="showAddFilterMenu"
      class="absolute top-10 left-0 z-40 w-48 rounded-xl border border-n-weak bg-n-solid-2 shadow-lg p-1"
    >
      <button
        v-for="filterConfig in availableFilterTypes"
        :key="filterConfig.type"
        class="w-full text-left h-8 px-2 rounded-md text-sm text-n-slate-12 hover:bg-n-alpha-1"
        type="button"
        @click.stop="selectFilterType(filterConfig.type)"
      >
        {{ filterConfig.label }}
      </button>
      <p
        v-if="!availableFilterTypes.length"
        class="m-0 px-2 py-1 text-xs text-n-slate-10"
      >
        {{ $t('FILTER.QA_PRESETS.NO_FILTERS') }}
      </p>
    </div>

    <div
      v-if="showValueMenu && currentConfig"
      class="absolute top-10 left-0 z-40 w-72 rounded-xl border border-n-weak bg-n-solid-2 shadow-lg"
    >
      <div class="px-3 py-2 border-b border-n-weak">
        <p class="m-0 text-sm font-semibold text-n-slate-12">
          {{ currentConfig.label }}
        </p>
      </div>
      <div
        v-if="currentConfig.enableSearch"
        class="px-2 py-2 border-b border-n-weak"
      >
        <input
          v-model="searchTerm"
          type="text"
          class="w-full h-8 px-2 rounded-md bg-n-solid-1 border border-n-weak text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
          :placeholder="$t('FILTER.INPUT_PLACEHOLDER')"
        />
      </div>
      <div class="max-h-72 overflow-y-auto p-1">
        <button
          v-for="option in filteredOptions"
          :key="`${activeFilterType}_${option.id}`"
          class="w-full text-left h-8 px-2 rounded-md text-sm text-n-slate-12 hover:bg-n-alpha-1"
          type="button"
          @click.stop="selectFilterOption(option)"
        >
          {{ option.name }}
        </button>
        <p
          v-if="!filteredOptions.length"
          class="m-0 px-2 py-1 text-xs text-n-slate-10"
        >
          {{ $t('FILTER.QA_PRESETS.NO_RESULTS') }}
        </p>
      </div>
    </div>
  </div>
</template>
